import type { ComponentProps, ImgHTMLAttributes } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import {
  alignItems,
  border,
  borderColor,
  borderRadius,
  display,
  height,
  justifyContent,
  overflow,
  position,
  size as styledSize,
  themeGet,
  width,
} from 'styled-system';

import { boxSizing, objectFit } from '../../theme/system-utilities';
import { getImageURLForCDN } from '../../utils/cdn/image';
import { mapResponsiveValue } from '../../utils/style';

interface ImageOptionProps {
  url: Nullable<string>;
  alt: string;
  disabled?: boolean;
  className?: string;
  /* removes the gaps between the image and the border */
  noGaps?: boolean;
  size?: number | number[];
  gapSize?: number;
  dataType?: string;
  onClick?: () => void;
  isSelected?: boolean;
  loading?: ImgHTMLAttributes<HTMLImageElement>['loading'];
  fillImage?: boolean;
}

interface GetImagePropsOptions extends Pick<ImageOptionProps, 'url' | 'noGaps'> {
  size: number | number[];
  gapSize: number;
}

type GetImageSizeOptions = Pick<GetImagePropsOptions, 'size' | 'gapSize' | 'noGaps'>;

const DEFAULT_IMAGE_SIZE = 26;
const DEFAULT_GAP_SIZE = 4;

const UnstyledImageHolderWrapper = styled.div<any>`
  ${border};
  ${borderRadius};
  ${position};
  ${styledSize};
  ${justifyContent};
  ${display};
  ${alignItems};
  ${overflow}

  ${props =>
    !!props.disabled &&
    css`
      &::after {
        background-color: ${themeGet('colors.sys.neutral.background.default')(props)};
        content: '';
        display: block;
        width: calc(100% - 6px);
        height: 2px;
        position: absolute;
        top: calc(50% - 1px);
        left: 3px;
        transform: rotate(45deg);
      }
    `};

  ${props =>
    !!props.disabled &&
    props.noGaps &&
    css`
      &::after {
        width: 100%;
        left: 0;
      }
    `};

  ${props =>
    props.noGaps
      ? css`
          border-style: solid;
          border-width: ${themeGet('borderWidths.small')(props)};
          border-color: ${themeGet('colors.sys.neutral.border.weak')(props)};
        `
      : css`
          padding: ${props.gapSize / 2}px;
          border-style: solid;
          border-width: ${themeGet('borderWidths.small')(props)};
          border-color: ${themeGet('colors.sys.neutral.border.inactive')(props)};
        `}

  ${props =>
    props.isSelected &&
    css`
      border: 2px solid ${themeGet('colors.sys.primary.border.default')(props)};
    `}
    
  ${props =>
    props.fillImage &&
    css`
      border-style: solid;
      border-width: ${themeGet('borderWidths.medium')(props)};
      border-color: white;
      outline: 1px solid ${themeGet('colors.sys.neutral.border.inactive')(props)};
    `}
`;

const ImageHolderWrapper = (props: ComponentProps<typeof UnstyledImageHolderWrapper>) => (
  <UnstyledImageHolderWrapper
    borderRadius="circle"
    position="relative"
    display="flex"
    justifyContent="center"
    alignItems="center"
    overflow="hidden"
    {...props}
  />
);

const UnstyledImage = styled.img<any>`
  ${display};
  ${styledSize};
  ${borderRadius};
  ${objectFit};
  ${boxSizing};
  ${border};
  ${borderColor};
  ${width};
  ${height}

  ${props =>
    props.fillImage &&
    css`
      object-fit: contain;
      width: 125%;
      height: 125%;
    `}
`;

const Image = (props: ComponentProps<typeof UnstyledImage>) => (
  <UnstyledImage display="inline-block" borderRadius="circle" objectFit="none" {...props} />
);

const getLastResponsiveValue = (value: number[] | number) =>
  !Array.isArray(value) ? value : value[value.length - 1];

const getImageUrl = ({
  url,
  size,
  gapSize,
  noGaps,
}: GetImageSizeOptions & Pick<ImageOptionProps, 'url'>) =>
  getImageURLForCDN({
    service: 'attribute',
    height: getLastResponsiveValue(size) + gapSize * (noGaps ? 3 : 2),
    width: getLastResponsiveValue(size) + gapSize * (noGaps ? 3 : 2),
    imageName: url ?? '',
  });

const getImageSize = ({ size, gapSize, noGaps }: GetImageSizeOptions) => {
  if (!noGaps) {
    return size;
  }

  return mapResponsiveValue(size, value => (value ? value + gapSize : value));
};

const getImageProps = ({ url, size, gapSize, noGaps }: GetImagePropsOptions) =>
  url
    ? { src: getImageUrl({ url, size, gapSize, noGaps }) }
    : {
        as: 'span',
        boxSizing: 'border-box',
        border: '2px dashed',
        borderColor: 'sys.neutral.border.active',
      };

const ImageOption = ({
  disabled,
  url,
  size = DEFAULT_IMAGE_SIZE,
  gapSize = DEFAULT_GAP_SIZE,
  noGaps = false,
  dataType,
  onClick,
  className,
  isSelected,
  loading,
  fillImage = false,
}: ImageOptionProps) => {
  const imageSize = getImageSize({ size, gapSize, noGaps });
  const imgProps = getImageProps({ url, size, gapSize, noGaps });

  return (
    <ImageHolderWrapper
      className={className}
      disabled={disabled}
      noGaps={noGaps}
      size={imageSize}
      gapSize={gapSize}
      isSelected={isSelected}
      fillImage={fillImage}
      {...(dataType ? { 'data-type': dataType } : {})}
      {...(onClick ? { onClick } : {})}
    >
      <Image
        data-testid="image-option"
        size={imageSize}
        loading={loading}
        fillImage={fillImage}
        alt=""
        {...imgProps}
      />
    </ImageHolderWrapper>
  );
};

export default ImageOption;
